/**
 * Authentication Service Layer
 * 
 * SOLID Principles Applied:
 * - Single Responsibility: Only handles auth-related API calls
 * - Dependency Inversion: Depends on axios abstraction
 * - Open/Closed: Easy to add new auth methods without modifying existing ones
 * - Interface Segregation: Clean interface for auth operations
 * 
 * KISS Principle: Each method does one thing clearly
 * 
 * Components should never call axios directly - they use this service.
 */

import axiosInstance from './axiosInstance';
import { tokenStore } from '../tokenStore';
import {
  IAuthResponse,
  ILoginDto,
  IRegisterDto,
  IResetPasswordDto,
  IChangePasswordDto,
  IUser,
  ICheckEmailResult,
  IForgotPasswordResponse,
  IActivateAccountDto,
} from '../../types/auth.types';
import { AxiosResponse } from 'axios';

/**
 * Auth Service Interface (for testing and extensibility)
 */
export interface IAuthService {
  login(credentials: ILoginDto): Promise<IAuthResponse>;
  register(userData: IRegisterDto): Promise<IAuthResponse>;
  logout(): Promise<void>;
  refreshToken(refreshToken: string): Promise<IAuthResponse>;
  getCurrentUser(): Promise<IUser>;
  forgotPassword(email: string): Promise<IForgotPasswordResponse>;
  resetPassword(data: IResetPasswordDto): Promise<void>;
  changePassword(data: IChangePasswordDto): Promise<void>;
  activateAccount(data: IActivateAccountDto): Promise<IAuthResponse>;
  activateTempSession(password: string): Promise<IAuthResponse>;
  prepareGoogleLink(): Promise<void>;
}

/**
 * Auth Service Implementation
 */
class AuthService implements IAuthService {
  private readonly baseUrl = '/auth';

  /**
   * Login user
   * POST /auth/login
   */
  async login(credentials: ILoginDto): Promise<IAuthResponse> {
    const response: AxiosResponse<IAuthResponse> = await axiosInstance.post(
      `${this.baseUrl}/login`,
      credentials
    );

    if (response.data.requiresTwoFactor) {
      return response.data;
    }
    
    // Store access token in memory only; refresh token is httpOnly cookie
    if (response.data.access_token) {
      tokenStore.setAccessToken(response.data.access_token);
    }
    
    return response.data;
  }

  /**
   * Register new user
   * POST /auth/register
   */
  async register(userData: IRegisterDto): Promise<IAuthResponse> {
    const response: AxiosResponse<IAuthResponse> = await axiosInstance.post(
      `${this.baseUrl}/register`,
      userData
    );
    
    // Store access token in memory only; refresh token is httpOnly cookie
    if (response.data.access_token) {
      tokenStore.setAccessToken(response.data.access_token);
    }
    
    return response.data;
  }

  /**
   * Login with Google One Tap credential (GIS JWT).
   * POST /auth/google/one-tap
   */
  async loginWithGoogleOneTap(credential: string): Promise<IAuthResponse> {
    const response: AxiosResponse<IAuthResponse> = await axiosInstance.post(
      `${this.baseUrl}/google/one-tap`,
      { credential },
    );

    if (response.data.requiresTwoFactor) {
      return response.data;
    }

    if (response.data.access_token) {
      tokenStore.setAccessToken(response.data.access_token);
    }

    return response.data;
  }

  /**
   * Logout user
   * POST /auth/logout
   */
  async logout(): Promise<void> {
    try {
      await axiosInstance.post(`${this.baseUrl}/logout`);
    } finally {
      // Always clear in-memory token, even if API call fails
      this.clearAuthData();
    }
  }

  /**
   * Refresh access token
   * POST /auth/refresh
   */
  async refreshToken(): Promise<IAuthResponse> {
    // No need to send refresh token - it's in httpOnly cookie!
    // Browser automatically sends it with the request
    const response: AxiosResponse<IAuthResponse> = await axiosInstance.post(
      `${this.baseUrl}/refresh`
    );
    
    // Update access token in memory; refresh cookie is handled by backend
    if (response.data.access_token) {
      tokenStore.setAccessToken(response.data.access_token);
    }
    
    return response.data;
  }

  /**
   * Get current authenticated user
   * GET /auth/me
   */
  async getCurrentUser(): Promise<IUser> {
    const response: AxiosResponse<IUser> = await axiosInstance.get(
      `${this.baseUrl}/me`
    );
    return response.data;
  }

  /**
   * Request password reset
   * POST /auth/forgot-password
   */
  async forgotPassword(email: string): Promise<IForgotPasswordResponse> {
    const response = await axiosInstance.post<IForgotPasswordResponse>(
      `${this.baseUrl}/forgot-password`,
      { email: email.trim().toLowerCase() },
    );
    return response.data;
  }

  /**
   * Reset password with token
   * POST /auth/reset-password
   */
  async resetPassword(data: IResetPasswordDto): Promise<void> {
    await axiosInstance.post(`${this.baseUrl}/reset-password`, {
      token: data.token,
      password: data.password,
    });
  }

  /**
   * Activate account with email link token
   * POST /auth/activate
   */
  async activateAccount(data: IActivateAccountDto): Promise<IAuthResponse> {
    if (!data.token?.trim()) {
      throw new Error('Activation token is required');
    }
    const response: AxiosResponse<IAuthResponse> = await axiosInstance.post(
      `${this.baseUrl}/activate`,
      { token: data.token.trim(), password: data.password },
    );
    if (response.data.access_token) {
      tokenStore.setAccessToken(response.data.access_token);
    }
    return response.data;
  }

  /**
   * Set password for guest session (post-checkout temporary JWT)
   * POST /auth/activate-temp-session
   */
  async activateTempSession(password: string): Promise<IAuthResponse> {
    const response: AxiosResponse<IAuthResponse> = await axiosInstance.post(
      `${this.baseUrl}/activate-temp-session`,
      { password },
    );
    if (response.data.access_token) {
      tokenStore.setAccessToken(response.data.access_token);
    }
    return response.data;
  }

  /**
   * Change password (when logged in)
   * POST /auth/change-password
   */
  async changePassword(data: IChangePasswordDto): Promise<void> {
    await axiosInstance.post(`${this.baseUrl}/change-password`, data);
  }

  /**
   * Prepare linking Google to the signed-in account (sets short-lived server cookie).
   * POST /auth/google/link/prepare
   */
  async prepareGoogleLink(): Promise<void> {
    await axiosInstance.post(`${this.baseUrl}/google/link/prepare`);
  }

  /**
   * Clear all authentication data from localStorage
   * Helper method for logout and session cleanup
   * Note: Refresh token cookie is cleared by backend
   */
  private clearAuthData(): void {
    tokenStore.clear();
  }

  /**
   * Check if user has valid token in localStorage
   * Utility method for initial app load
   */
  hasStoredAuth(): boolean {
    return !!tokenStore.getAccessToken();
  }

  /**
   * Get stored access token
   * Utility method for session restoration
   * Note: Refresh token is in httpOnly cookie, not accessible to JS
   */
  getStoredAccessToken(): string | null {
    return tokenStore.getAccessToken();
  }

  /**
   * Check if email exists in system
   * POST /auth/check-email
   */
  async checkEmail(email: string): Promise<ICheckEmailResult> {
    const response = await axiosInstance.post<ICheckEmailResult>(
      `${this.baseUrl}/check-email`,
      { email: email.trim().toLowerCase() },
    );
    return response.data;
  }

  /**
   * Silent registration - Creates account automatically after guest checkout
   * Sends welcome email with auto-generated password
   * POST /auth/silent-register
   */
  async silentRegister(params: {
    email: string;
    name: string;
    phone?: string;
    orderId?: string;
  }): Promise<{
    success: boolean;
    userExists: boolean;
    userId: string;
    linkedOrders?: number;
    linkedDesigns?: number;
    temporaryToken?: string;
    needsPasswordSetup?: boolean;
    message: string;
  }> {
    const response = await axiosInstance.post(`${this.baseUrl}/silent-register`, params);
    return response.data;
  }
}

// Export singleton instance (KISS - simple to use)
export const authService = new AuthService();

// Also export class for testing/mocking
export default AuthService;

